// src/services/CartPurchaseHandler.js - Clean and properly structured
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  checkMilestoneEmailPending,
  notifyMilestoneAchievement,
  markMilestoneEmailSent,
} from "./MilestoneService";
import {
  AUTO_FILL_UPGRADE_AMOUNT,
  CURRENT_ACTIVATION_FEE,
  isCurrentTierActivationFee,
} from "../config/membershipConfig";
import {
  membershipCashbackTopUpAmount,
  resolveFirstTimeMembershipCashbackAmount,
} from "../utils/membershipCashback";
import { createCashOutProgramsForOrder } from "./cashOut/CashOutProgramService";
import MultiLevelCommissionService from "./MultiLevelCommissionService";
import { stripUndefinedForFirestore } from "../utils/stripUndefinedForFirestore";
import { calculateCartProfitMargin } from "../utils/cartProfitMargin";

// Main Cart Purchase Handler
class CartPurchaseHandler {
  static async addFirstTimeCashback(
    userId,
    amount = CURRENT_ACTIVATION_FEE
  ) {
    try {
      const credit = Number(amount);
      if (!credit || credit <= 0) {
        console.log("❌ Invalid first-time cashback amount:", amount);
        return false;
      }

      console.log("=== ADDING FIRST TIME CASHBACK ===");
      console.log("User ID:", userId, "Amount:", credit);

      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const userData = userSnapshot.docs[0].data();

        console.log("Current user data:");
        console.log("- hasPurchasedPackage:", userData.hasPurchasedPackage);
        console.log("- cashbackEarned:", userData.cashbackEarned || 0);
        console.log("- cashbackBalance:", userData.cashbackBalance || 0);

        // FIXED LOGIC: Check if cashback hasn't been given yet (regardless of hasPurchasedPackage status)
        const cashbackEarned = userData.cashbackEarned || 0;

        if (cashbackEarned === 0) {
          console.log("✅ User eligible for first-time cashback");

          await updateDoc(userDocRef, {
            cashbackBalance: credit,
            cashbackEarned: credit,
            cashbackUsed: 0, // Initialize if not exists
            membershipCashbackActivationTier: credit,
            updatedAt: new Date().toISOString(),
          });

          console.log(
            `✅ Added ₹${credit} cashback to user ${userId} for first package purchase`
          );

          // Verify the update
          const verifySnapshot = await getDocs(userQuery);
          const verifiedData = verifySnapshot.docs[0].data();
          console.log(
            "Verified cashback balance:",
            verifiedData.cashbackBalance
          );

          return true;
        } else {
          console.log("❌ User already has cashback earned:", cashbackEarned);
          return false;
        }
      } else {
        console.log("❌ User document not found");
        return false;
      }
    } catch (error) {
      console.error("❌ Error adding first-time cashback:", error);
      throw error;
    }
  }

  /** Top up cashback when user is on current Ultra tier but earned below full fee. */
  static async topUpMembershipCashbackTo649Tier(userId) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);
      if (userSnapshot.empty) return false;

      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();

      if (userData.membershipCashback649TopUp === true) return false;

      const activationFee =
        userData.purchasedActivationFee ??
        userData.purchasedPackage?.activationFee;
      if (!isCurrentTierActivationFee(activationFee)) return false;

      const topUp = membershipCashbackTopUpAmount(
        userData.cashbackEarned,
        CURRENT_ACTIVATION_FEE
      );
      if (topUp <= 0) return false;

      await updateDoc(userDocRef, {
        cashbackBalance: (userData.cashbackBalance || 0) + topUp,
        cashbackEarned: (userData.cashbackEarned || 0) + topUp,
        membershipCashback649TopUp: true,
        membershipCashback649TopUpAmount: topUp,
        membershipCashback649TopUpAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      console.log(
        `✅ Topped up ₹${topUp} membership cashback for user ${userId} (Ultra tier)`
      );
      return true;
    } catch (error) {
      console.error("❌ Error topping up membership cashback:", error);
      throw error;
    }
  }

  /** Credit upgrade cashback when Auto Filling Program upgrade is confirmed. */
  static async addAutoFillUpgradeCashback(
    userId,
    amount = AUTO_FILL_UPGRADE_AMOUNT
  ) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        console.log("❌ User not found for auto-fill upgrade cashback");
        return false;
      }

      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();

      if (userData.autoFillUpgradeCashbackCredited === true) {
        console.log("⏭️ Auto-fill upgrade cashback already credited");
        return false;
      }

      const credit = Number(amount);
      if (!credit || credit <= 0) return false;

      await updateDoc(userDocRef, {
        cashbackBalance: (userData.cashbackBalance || 0) + credit,
        cashbackEarned: (userData.cashbackEarned || 0) + credit,
        autoFillUpgradeCashbackCredited: true,
        autoFillUpgradeCashbackAmount: credit,
        autoFillUpgradeCashbackAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      console.log(
        `✅ Added ₹${credit} auto-fill upgrade cashback to user ${userId}`
      );
      return true;
    } catch (error) {
      console.error("❌ Error adding auto-fill upgrade cashback:", error);
      throw error;
    }
  }

  static async updateCashbackBalance(
    userId,
    cashbackAmount,
    operation = "deduct"
  ) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const userData = userSnapshot.docs[0].data();

        const currentBalance = userData.cashbackBalance || 0;
        const currentUsed = userData.cashbackUsed || 0;

        if (operation === "deduct") {
          const updateData = {
            cashbackBalance: Math.max(0, currentBalance - cashbackAmount),
            cashbackUsed: currentUsed + cashbackAmount,
            updatedAt: new Date().toISOString(),
          };

          await updateDoc(userDocRef, updateData);
          console.log(
            `Updated cashback balance for user ${userId}: -₹${cashbackAmount}`
          );
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error updating cashback balance:", error);
      throw error;
    }
  }

  static async updateCommissionBalance(
    userId,
    commissionAmount,
    operation = "deduct"
  ) {
    try {
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const userData = userSnapshot.docs[0].data();

        const currentBalance = userData.commissionBalance || 0;
        const currentUsed = userData.commissionUsed || 0;

        if (operation === "deduct") {
          const updateData = {
            commissionBalance: Math.max(0, currentBalance - commissionAmount),
            commissionUsed: currentUsed + commissionAmount,
            updatedAt: new Date().toISOString(),
          };

          await updateDoc(userDocRef, updateData);
          console.log(
            `Updated commission balance for user ${userId}: -₹${commissionAmount}`
          );
          return true;
        } else if (operation === "add") {
          const currentEarned = userData.commissionEarned || 0;
          const updateData = {
            commissionBalance: currentBalance + commissionAmount,
            commissionEarned: currentEarned + commissionAmount,
            updatedAt: new Date().toISOString(),
          };

          await updateDoc(userDocRef, updateData);
          console.log(
            `Updated commission balance for user ${userId}: +₹${commissionAmount}`
          );
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error updating commission balance:", error);
      throw error;
    }
  }

  // Confirm a cart order and process affiliate system commissions
  static async confirmCartOrder(cartOrderId) {
    try {
      console.log("=== CONFIRMING CART ORDER WITH CASHBACK DEBUG ===");
      console.log("Cart Order ID:", cartOrderId);

      // Find the order
      const orderQuery = query(
        collection(db, "cartOrders"),
        where("cartOrderId", "==", cartOrderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        return { success: false, error: "Order not found" };
      }

      const orderDoc = orderSnapshot.docs[0];
      const orderData = orderDoc.data();
      const orderRef = doc(db, "cartOrders", orderDoc.id);

      console.log("Order found:", orderData.cartOrderId);
      console.log("Buyer ID:", orderData.buyerId);
      console.log("Has packages:", (orderData.packages || []).length > 0);

      // Check existing order status
      if (orderData.orderStatus === "confirmed") {
        return { success: false, error: "Order is already confirmed" };
      }

      if (
        orderData.orderStatus === "rejected" ||
        orderData.orderStatus === "cancelled"
      ) {
        return {
          success: false,
          error: `Cannot confirm ${orderData.orderStatus} order`,
        };
      }

      // Step 1: Update order status to confirmed
      await updateDoc(orderRef, {
        orderStatus: "confirmed",
        paymentStatus: "confirmed",
        confirmedAt: new Date().toISOString(),
        confirmedBy: "admin",
        updatedAt: new Date().toISOString(),
      });

      console.log("✅ Order status updated to confirmed");

      // Step 2: Handle package purchase and cashback
      // Also check for item purchase upgrade (₹6999+ items = Ultra customer)
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", orderData.buyerId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const userData = userSnapshot.docs[0].data();
        const isCurrentlyUltra = userData.hasPurchasedPackage || false;

        // Check if this is a package purchase
        const hasPackages = (orderData.packages && orderData.packages.length > 0) || orderData.isPackagePurchase;
        
        // Calculate total item amount (excluding packages)
        const itemTotal = (orderData.items || []).reduce((sum, item) => {
          const itemPrice = item.sellingPrice || item.price || 0;
          const quantity = item.quantity || 1;
          return sum + (itemPrice * quantity);
        }, 0);

        console.log("Order analysis:");
        console.log("- Is currently Ultra:", isCurrentlyUltra);
        console.log("- Has packages:", hasPackages);
        console.log("- Item total:", itemTotal);

        // Scenario 1: Package purchase - standard flow
        if (hasPackages) {
          console.log("Processing package purchase...");

          const isFirstPackagePurchase = !isCurrentlyUltra;
          const hasNoCashback = (userData.cashbackEarned || 0) === 0;

          // Update hasPurchasedPackage status first
          await updateDoc(userDocRef, {
            hasPurchasedPackage: true,
            packagePurchaseDate: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });

          // Add first-time cashback if eligible
          if (isFirstPackagePurchase && hasNoCashback) {
            try {
              const pkg = orderData.packages?.[0];
              const cashbackAmount = pkg
                ? resolveFirstTimeMembershipCashbackAmount(orderData, pkg)
                : CURRENT_ACTIVATION_FEE;
              await this.addFirstTimeCashback(
                orderData.buyerId,
                cashbackAmount
              );
              console.log(
                `✅ Successfully added ₹${cashbackAmount} first-time package cashback`
              );
              
              // Check for milestone achievements for referrers
              await this.checkReferrerMilestones(orderData.buyerId);
            } catch (cashbackError) {
              console.error(
                "❌ Error adding first-time cashback:",
                cashbackError
              );
            }
          }
        }
        // Scenario 2: Non-Ultra customer with item purchase >= ₹6999 → Upgrade to Ultra
        else if (!isCurrentlyUltra && itemTotal >= 6999) {
          console.log(`🎉 ITEM PURCHASE UPGRADE: User spent ₹${itemTotal} on items (>= ₹6999)`);
          console.log("Upgrading to Ultra customer...");

          const hasNoCashback = (userData.cashbackEarned || 0) === 0;

          // Update to Ultra customer status
          await updateDoc(userDocRef, {
            hasPurchasedPackage: true,
            packagePurchaseDate: new Date().toISOString(),
            ultraUpgradeReason: "item_purchase_6999_plus",
            ultraUpgradeOrderId: orderData.cartOrderId,
            ultraUpgradeItemTotal: itemTotal,
            updatedAt: new Date().toISOString(),
          });

          console.log("✅ User upgraded to Ultra customer via item purchase!");

          // Check for milestone achievements for referrers
          await this.checkReferrerMilestones(orderData.buyerId);

          // Add first-time cashback if eligible (same as package purchase)
          if (hasNoCashback) {
            try {
              await this.addFirstTimeCashback(
                orderData.buyerId,
                CURRENT_ACTIVATION_FEE
              );
              console.log(
                `✅ Successfully added ₹${CURRENT_ACTIVATION_FEE} cashback for Ultra upgrade via items`
              );
            } catch (cashbackError) {
              console.error(
                "❌ Error adding cashback for item upgrade:",
                cashbackError
              );
            }
          }
        }
      }

      // Step 3: Process affiliate system commissions for ALL orders (items + packages)
      console.log("Processing affiliate system commissions...");
      const commissionResults =
        await MultiLevelCommissionService.processOrderCommissions({
          cartOrderId: orderData.cartOrderId,
          buyerId: orderData.buyerId,
          buyerName: orderData.buyerName,
          buyerEmail: orderData.buyerEmail,
          items: orderData.items || [],
          packages: orderData.packages || [],
          totalAmount: orderData.totalAmount || orderData.grandTotal,
          orderCounts: orderData.orderCounts,
          appliedDiscounts: orderData.appliedDiscounts || {},
          referralSharingBlocked:
            orderData.referralSharingBlocked === true ||
            orderData.appliedDiscounts?.usedCashback === true ||
            (orderData.appliedDiscounts?.cashbackDiscount || 0) > 0,
        });

      console.log("Commission processing result:", commissionResults.success);
      if (commissionResults.success) {
        console.log(`✅ Affiliate system commissions processed successfully:`);
        console.log(
          `- Total commissions created: ${commissionResults.totalCommissionsCreated}`
        );
        console.log(
          `- Total commission amount: ₹${commissionResults.totalCommissionAmount.toFixed(
            2
          )}`
        );
      }

      return {
        success: true,
        message:
          "Order confirmed successfully with cashback and commission processing",
        orderId: cartOrderId,
        commissionsProcessed: commissionResults.success,
        totalCommissionsCreated: commissionResults.totalCommissionsCreated || 0,
        totalCommissionAmount: commissionResults.totalCommissionAmount || 0,
      };
    } catch (error) {
      console.error("❌ Error confirming cart order:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Check for milestone achievements for referrers when someone becomes Ultra
  static async checkReferrerMilestones(newUltraCustomerId) {
    try {
      // Find who referred this new Ultra customer
      const newUltraQuery = query(
        collection(db, "users"),
        where("uid", "==", newUltraCustomerId)
      );
      const newUltraSnapshot = await getDocs(newUltraQuery);
      
      if (newUltraSnapshot.empty) return;
      
      const newUltraData = newUltraSnapshot.docs[0].data();
      const referralIdUsed = newUltraData.referralIdUsed;
      
      if (!referralIdUsed) return;
      
      // Find the referrer
      const referrerQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralIdUsed)
      );
      const referrerSnapshot = await getDocs(referrerQuery);
      
      if (referrerSnapshot.empty) return;
      
      const referrerData = referrerSnapshot.docs[0].data();
      
      // Check milestones for the referrer
      const emailPending = await checkMilestoneEmailPending(
        referrerData.uid,
        referrerData
      );
      if (emailPending) {
        console.log(
          `🎉 Milestone email for referrer ${referrerData.uid}: ${emailPending.milestone} Ultra customers`
        );
        await notifyMilestoneAchievement(
          referrerData,
          emailPending.milestone
        );
        await markMilestoneEmailSent(
          referrerData.uid,
          emailPending.milestone
        );
      }
    } catch (error) {
      console.error("Error checking referrer milestones:", error);
    }
  }

  // Update buyer's package purchase status
  static async updateBuyerPackageStatus(buyerId) {
    try {
      console.log("Updating buyer package purchase status for:", buyerId);

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", buyerId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        // DEBUG: Log before update
        console.log("Current user data:", userSnapshot.docs[0].data());

        await updateDoc(userDocRef, {
          hasPurchasedPackage: true, // ← This is crucial
          packagePurchaseDate: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        console.log("✅ User package purchase status updated successfully");

        // DEBUG: Verify update
        const updatedSnapshot = await getDocs(userQuery);
        console.log("Updated user data:", updatedSnapshot.docs[0].data());
      } else {
        console.warn("❌ Buyer not found for package status update");
      }
    } catch (error) {
      console.error("❌ Error updating buyer package status:", error);
    }
  }

  // Reject order and handle any existing commissions
  static async rejectCartOrder(cartOrderId, rejectionReason) {
    try {
      console.log("Rejecting cart order:", cartOrderId);

      // Find the order
      const orderQuery = query(
        collection(db, "cartOrders"),
        where("cartOrderId", "==", cartOrderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        return {
          success: false,
          error: "Order not found",
        };
      }

      const orderDoc = orderSnapshot.docs[0];
      const orderData = orderDoc.data();
      const orderRef = doc(db, "cartOrders", orderDoc.id);

      // Check if order is already processed
      if (orderData.orderStatus === "confirmed") {
        return {
          success: false,
          error: "Cannot reject confirmed order",
        };
      }

      if (orderData.orderStatus === "rejected") {
        return {
          success: false,
          error: "Order is already rejected",
        };
      }

      // Update order status to rejected
      await updateDoc(orderRef, {
        orderStatus: "rejected",
        paymentStatus: "rejected",
        rejectedAt: new Date().toISOString(),
        rejectedBy: "admin",
        rejectionReason: rejectionReason,
        updatedAt: new Date().toISOString(),
      });

      console.log("Order status updated to rejected");

      return {
        success: true,
        message: "Order rejected successfully",
        orderId: cartOrderId,
        rejectionReason: rejectionReason,
      };
    } catch (error) {
      console.error("Error rejecting cart order:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get order with commission details
  static async getOrderWithCommissions(cartOrderId) {
    try {
      // Get order details
      const orderQuery = query(
        collection(db, "cartOrders"),
        where("cartOrderId", "==", cartOrderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        return {
          success: false,
          error: "Order not found",
        };
      }

      const orderData = orderSnapshot.docs[0].data();

      // Get commissions for this order
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("orderId", "==", cartOrderId),
        where("isMultiLevelCommission", "==", true),
        orderBy("commissionLevel", "asc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);

      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return {
        success: true,
        order: orderData,
        commissions: commissions,
      };
    } catch (error) {
      console.error("Error getting order with commissions:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Calculate potential commission for an order (before confirmation)
  static async calculatePotentialCommissions(orderData) {
    try {
      // Get referral chain for the buyer
      const referralChain =
        await MultiLevelCommissionService.getUserReferralChain(
          orderData.buyerId
        );

      if (referralChain.length === 0) {
        return {
          success: true,
          hasReferralChain: false,
          potentialCommissions: [],
          totalPotentialCommission: 0,
          estimatedRoyalty: 0,
        };
      }

      const potentialCommissions = [];
      let totalPotentialCommission = 0;
      let totalProfit = 0;

      // Calculate for items
      if (orderData.items) {
        for (const item of orderData.items) {
          if (item.referralShare) {
            const itemProfit = (item.profitMargin || 0) * (item.quantity || 1);
            totalProfit += itemProfit;

            for (const referrer of referralChain) {
              if (referrer.hasPurchasedPackage) {
                const commissionAmount =
                  (itemProfit * referrer.commissionRate) / 100;
                potentialCommissions.push({
                  type: "item",
                  itemName: item.name,
                  level: referrer.level,
                  referrerEmail: referrer.referrerEmail,
                  commissionRate: referrer.commissionRate,
                  commissionAmount,
                  profit: itemProfit,
                });
                totalPotentialCommission += commissionAmount;
              }
            }
          }
        }
      }

      // Calculate for packages
      if (orderData.packages) {
        for (const pkg of orderData.packages) {
          if (pkg.referralShare) {
            const pkgProfit =
              (pkg.profitMargin || pkg.price * 0.1) * (pkg.quantity || 1);
            totalProfit += pkgProfit;

            for (const referrer of referralChain) {
              if (referrer.hasPurchasedPackage) {
                const commissionAmount =
                  (pkgProfit * referrer.commissionRate) / 100;
                potentialCommissions.push({
                  type: "package",
                  packageName: pkg.name,
                  level: referrer.level,
                  referrerEmail: referrer.referrerEmail,
                  commissionRate: referrer.commissionRate,
                  commissionAmount,
                  profit: pkgProfit,
                });
                totalPotentialCommission += commissionAmount;
              }
            }
          }
        }
      }

      const estimatedRoyalty = totalProfit - totalPotentialCommission;

      return {
        success: true,
        hasReferralChain: true,
        referralChainLength: referralChain.length,
        potentialCommissions,
        totalPotentialCommission:
          Math.round(totalPotentialCommission * 100) / 100,
        totalProfit: Math.round(totalProfit * 100) / 100,
        estimatedRoyalty: Math.round(estimatedRoyalty * 100) / 100,
        royaltyPercentage:
          totalProfit > 0
            ? ((estimatedRoyalty / totalProfit) * 100).toFixed(2)
            : 0,
      };
    } catch (error) {
      console.error("Error calculating potential commissions:", error);
      return {
        success: false,
        error: error.message,
        hasReferralChain: false,
        potentialCommissions: [],
        totalPotentialCommission: 0,
        estimatedRoyalty: 0,
      };
    }
  }

  static async validateDiscountApplication(
    userId,
    appliedDiscounts,
    cart = null
  ) {
    try {
      // Get user's current balances
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        return {
          valid: false,
          error: "User not found",
        };
      }

      const userData = userSnapshot.docs[0].data();
      const cashbackBalance = userData.cashbackBalance || 0;
      const commissionBalance = userData.commissionBalance || 0;

      const errors = [];

      // Validate cashback usage
      if (appliedDiscounts.usedCashback) {
        if (appliedDiscounts.cashbackDiscount > cashbackBalance) {
          errors.push(
            `Insufficient cashback balance (₹${cashbackBalance} available)`
          );
        }
        if (cart) {
          const profitCap = calculateCartProfitMargin(cart);
          if (appliedDiscounts.cashbackDiscount > profitCap) {
            errors.push(
              `Cashback cannot exceed ₹${profitCap.toFixed(
                2
              )} for this cart`
            );
          }
        }
      }

      // Validate commission usage
      if (appliedDiscounts.usedCommission) {
        const totalCommissionRequired =
          appliedDiscounts.commissionDiscount +
          appliedDiscounts.commissionAdminFee;
        if (totalCommissionRequired > commissionBalance) {
          errors.push(
            `Insufficient commission balance (₹${commissionBalance} available)`
          );
        }
      }

      return {
        valid: errors.length === 0,
        errors: errors,
        userBalances: {
          cashback: cashbackBalance,
          commission: commissionBalance,
        },
      };
    } catch (error) {
      console.error("Error validating discount application:", error);
      return {
        valid: false,
        error: error.message,
      };
    }
  }

  static async createCartOrderForPayment(cartData) {
    try {
      console.log("Creating cart order for payment...");

      // Generate order ID  
      const timestamp = Date.now();
      const cartOrderId = `CART-${timestamp}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Create minimal order record for payment
      const orderRef = await addDoc(
        collection(db, "cartOrders"),
        stripUndefinedForFirestore({
          cartOrderId,
          buyerId: cartData.buyerId,
          buyerName: cartData.buyerName,
          buyerEmail: cartData.buyerEmail,
          status: "pending_payment",
          amount: cartData.grandTotal,
          items: cartData.items || [],
          packages: cartData.packages || [],
          createdAt: serverTimestamp(),
          shippingAddress: cartData.shippingAddress || null,
          cashOutProgram: cartData.cashOutProgram || { enrolled: false },
        })
      );

      console.log("Cart order created for payment:", cartOrderId);
      return { 
        success: true, 
        orderId: cartOrderId, 
        docId: orderRef.id 
      };
    } catch (error) {
      console.error("Error creating cart order for payment:", error);
      return { success: false, error: error.message };
    }
  }

  static async processCartPurchase(cartData) {
    try {
      console.log("Processing cart purchase with discounts:", cartData);

      // Use provided order ID or generate new one
      const cartOrderId = cartData.orderId || `CART-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // UPDATED: Registration fee calculation (same as CartPage/CheckoutPage)
      const calculateRegistrationFee = (cartData) => {
        // No registration fee for admins (would need to pass isAdmin flag)
        // if (isAdmin) return 0;

        // No registration fee if user already purchased packages (eligible)
        // This would need userProfile data passed in cartData
        // if (userProfile?.hasPurchasedPackage) return 0;

        // Check if cart contains "Activation Fee" package
        const hasActivationFee = cartData.packages?.some(
          (pkg) => pkg.category === "Activation Fee"
        );

        if (hasActivationFee) {
          return 0; // Activation fee packages don't need additional registration fee
        }

        // COMPLETELY REMOVE registration fee - it's no longer charged
        return 0; // Changed from cartData.registrationFee to 0
      };

      // Calculate corrected registration fee
      const correctedRegistrationFee = calculateRegistrationFee(cartData);

      // Prepare order data with discount information
      const orderData = {
        cartOrderId,
        orderType: "cart_purchase",
        buyerId: cartData.buyerId,
        buyerName: cartData.buyerName,
        buyerEmail: cartData.buyerEmail,

        // Items and packages - Ensure GST data is preserved
        items: (cartData.items || []).map(item => ({
          ...item,
          // Ensure GST fields are preserved
          gstPercentage: item.gstPercentage !== undefined ? item.gstPercentage : (item.gstPercentage !== null && item.gstPercentage !== '' ? Number(item.gstPercentage) : 0),
          basePrice: item.basePrice || item.sellingPrice || item.price || 0,
          // Calculate GST amount per item if not already present
          gstAmount: item.gstAmount || (() => {
            const basePrice = item.basePrice || item.sellingPrice || item.price || 0;
            const gstPercentage = (item.gstPercentage !== undefined && item.gstPercentage !== null && item.gstPercentage !== '') ? Number(item.gstPercentage) : 0;
            const quantity = item.quantity || 1;
            return gstPercentage > 0 ? (basePrice * gstPercentage / 100) * quantity : 0;
          })(),
        })),
        packages: (cartData.packages || []).map(pkg => ({
          ...pkg,
          // Ensure GST fields are preserved for packages too
          gstPercentage: pkg.gstPercentage !== undefined ? pkg.gstPercentage : (pkg.gstPercentage !== null && pkg.gstPercentage !== '' ? Number(pkg.gstPercentage) : 0),
          basePrice: pkg.basePrice || pkg.price || 0,
          gstAmount: pkg.gstAmount || (() => {
            const basePrice = pkg.basePrice || pkg.price || 0;
            const gstPercentage = (pkg.gstPercentage !== undefined && pkg.gstPercentage !== null && pkg.gstPercentage !== '') ? Number(pkg.gstPercentage) : 0;
            const quantity = pkg.quantity || 1;
            return gstPercentage > 0 ? (basePrice * gstPercentage / 100) * quantity : 0;
          })(),
        })),

        // UPDATED: Totals without registration fee
        itemTotal: cartData.itemTotal || 0,
        packageTotal: cartData.packageTotal || 0,
        registrationFee: correctedRegistrationFee, // Always 0 now
        subtotal: (cartData.itemTotal || 0) + (cartData.packageTotal || 0), // Recalculate without registration fee

        // NEW: Add discount information to order data
        appliedDiscounts: cartData.appliedDiscounts || {
          cashbackDiscount: 0,
          commissionDiscount: 0,
          commissionAdminFee: 0,
          totalDiscount: 0,
          usedCashback: false,
          usedCommission: false,
        },
        referralSharingBlocked:
          cartData.appliedDiscounts?.usedCashback === true ||
          (cartData.appliedDiscounts?.cashbackDiscount || 0) > 0,
        
        // MRP and GST information for invoice generation
        totalMRP: cartData.totalMRP || 0,  // Total MRP before discounts
        gstAmount: cartData.gstAmount || 0,  // GST amount
        priceSavings: cartData.priceSavings || 0,  // Savings from MRP

        // Use the correct grandTotal passed from CartPage (includes delivery fees)
        grandTotal: cartData.grandTotal || (
          (cartData.itemTotal || 0) +
          (cartData.packageTotal || 0) -
          (cartData.appliedDiscounts?.totalDiscount || 0)
        ),
        totalAmount: cartData.grandTotal || (
          (cartData.itemTotal || 0) +
          (cartData.packageTotal || 0) -
          (cartData.appliedDiscounts?.totalDiscount || 0)
        ),

        // Order counts
        orderCounts: {
          totalItems:
            cartData.items?.reduce(
              (sum, item) => sum + (item.quantity || 1),
              0
            ) || 0,
          uniqueItems: cartData.items?.length || 0,
          totalPackages:
            cartData.packages?.reduce(
              (sum, pkg) => sum + (pkg.quantity || 1),
              0
            ) || 0,
          uniquePackages: cartData.packages?.length || 0,
        },

        // Payment details
        paymentMethod: cartData.paymentMethod || "manual_transfer",
        paymentDetails: cartData.paymentDetails || {},

        // Status
        orderStatus: "pending",
        paymentStatus: "pending",

        // User details
        userDetails: {
          myReferralId: cartData.userDetails?.myReferralId || null,
          referralIdUsed: cartData.userDetails?.referralIdUsed || null,
        },

        // Shipping address
        shippingAddress: cartData.shippingAddress || null,

        // Timestamps
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),

        // Additional metadata
        isCartPurchase: true,
        includesRegistrationFee: correctedRegistrationFee > 0,
        hasAppliedDiscounts:
          cartData.appliedDiscounts &&
          (cartData.appliedDiscounts.usedCashback ||
            cartData.appliedDiscounts.usedCommission),

        cashOutProgram: cartData.cashOutProgram || { enrolled: false },
        cashOutFee: cartData.cashOutProgram?.enrollmentFee || 0,

        cashRoyaltyTransfer:
          cartData.cashRoyaltyTransfer ||
          cartData.cesCoinsTransfer || { enabled: false },
      };

      const firestoreOrderData = stripUndefinedForFirestore(orderData);

      let firestoreDocId;
      const existingOrderSnap = await getDocs(
        query(
          collection(db, "cartOrders"),
          where("cartOrderId", "==", cartOrderId)
        )
      );

      if (!existingOrderSnap.empty) {
        const existingDoc = existingOrderSnap.docs[0];
        firestoreDocId = existingDoc.id;
        await updateDoc(doc(db, "cartOrders", firestoreDocId), {
          ...firestoreOrderData,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const orderRef = await addDoc(
          collection(db, "cartOrders"),
          firestoreOrderData
        );
        firestoreDocId = orderRef.id;
      }

      const orderRef = doc(db, "cartOrders", firestoreDocId);

      let cashOutProgramIds = [];
      const cop = cartData.cashOutProgram;
      if (cop?.enrolled && (cop.blockCount || 0) > 0) {
        const copResult = await createCashOutProgramsForOrder({
          userId: cartData.buyerId,
          userEmail: cartData.buyerEmail,
          userName: cartData.buyerName,
          cartOrderId,
          blockCount: cop.blockCount,
          enrollmentFee: cop.enrollmentFee,
          orderSubtotal: cop.orderSubtotal ?? cartData.subtotal,
        });
        cashOutProgramIds = copResult.programIds || [];
        if (cashOutProgramIds.length > 0) {
          await updateDoc(orderRef, {
            cashOutProgramIds,
            cashOutProgram: {
              ...cop,
              programIds: cashOutProgramIds,
              status: "created",
            },
            updatedAt: new Date().toISOString(),
          });
        }
      }

      console.log(
        "Cart order created successfully with discounts:",
        cartOrderId
      );
      console.log("Applied discounts:", orderData.appliedDiscounts);

      return {
        success: true,
        orderId: cartOrderId, // Keep for backward compatibility
        cartOrderId: cartOrderId, // ✅ ADD THIS LINE
        docId: orderRef.id,
        message: "Order placed and confirmed successfully.",
        appliedDiscounts: orderData.appliedDiscounts,
        orderCounts: orderData.orderCounts, // ✅ ADD THIS LINE TOO
        cashOutProgramIds,
      };
    } catch (error) {
      console.error("Error processing cart purchase:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }
}

export default CartPurchaseHandler;
